import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection } from '../lib/firestore'
import { useArtists } from '../lib/artists'
import { useEntitlements } from '../lib/entitlements'
import { formatDayLong } from '../lib/format'
import type {
  Flight,
  GuestListItem,
  Hotel,
  ItineraryItem,
  Show,
  Tour,
  TourCrew,
} from '../types/models'
import Page from '../components/Page'
import Icon from '../components/Icon'
import Empty from '../components/Empty'
import ExportDocument, {
  PRESETS,
  type DocumentData,
  type DocumentOptions,
  type Preset,
} from '../components/export/ExportDocument'
import { printDocument } from '../lib/print'

/* ============================================================================
   Export.

   Left: what to make. Right: exactly what will come out. The preview is the
   document — the same DOM the printer receives — so there is no "export and
   find out" step.

   Presets a plan doesn't include are shown, not hidden. Someone on the free
   tier should be able to see that a tour book exists; they just can't render
   one yet. Hiding it makes the product look smaller than it is and makes the
   upgrade meaningless.
   ============================================================================ */

export default function Export() {
  const { user, uid } = useAuth()
  const { artists } = useArtists()
  const { permits, plan, loaded } = useEntitlements()
  const [params, setParams] = useSearchParams()

  const tours = useCollection<Tour>('tours', user ? [where('ownerId', '==', uid)] : null)

  const [tourId, setTourId] = useState<string | null>(params.get('tour'))
  const [preset, setPreset] = useState<Preset>((params.get('preset') as Preset) ?? 'show')
  const [showId, setShowId] = useState<string | null>(params.get('show'))
  const [includeCrew, setIncludeCrew] = useState(true)
  const [includeNotes, setIncludeNotes] = useState(false)
  const [notes, setNotes] = useState('')

  // Default to the tour that is running, or failing that the most recent one.
  useEffect(() => {
    if (tourId || tours.data.length === 0) return
    const now = Date.now()
    const live = tours.data.find(
      (tour) => tour.startDate.toMillis() <= now && tour.endDate.toMillis() >= now,
    )
    const newest = [...tours.data].sort(
      (a, b) => b.startDate.toMillis() - a.startDate.toMillis(),
    )[0]
    setTourId((live ?? newest)?.id ?? null)
  }, [tours.data, tourId])

  // Deep links are the whole point of the share sheet, so selection lives in
  // the URL as well as in state.
  useEffect(() => {
    const next = new URLSearchParams(params)
    if (tourId) next.set('tour', tourId)
    else next.delete('tour')
    next.set('preset', preset)
    if (showId) next.set('show', showId)
    else next.delete('show')
    setParams(next, { replace: true })
    // `params` is deliberately absent: including it re-runs this on its own
    // write and loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId, preset, showId])

  const tour = tours.data.find((entry) => entry.id === tourId) ?? null
  const scope = tourId ? [where('tourId', '==', tourId)] : null

  const shows = useCollection<Show>('shows', scope)
  const itinerary = useCollection<ItineraryItem>('itineraryItems', scope)
  const flights = useCollection<Flight>('flights', scope)
  const hotels = useCollection<Hotel>('hotels', scope)
  const crew = useCollection<TourCrew>('tourCrew', scope)

  // Guest lists are a subcollection of the show, so this only subscribes once
  // a show is actually selected.
  const guests = useCollection<GuestListItem>(
    showId ? `shows/${showId}/guestlist` : 'shows',
    showId ? [] : null,
  )

  const sortedShows = useMemo(
    () => [...shows.data].sort((a, b) => a.date.toMillis() - b.date.toMillis()),
    [shows.data],
  )

  const spec = PRESETS.find((entry) => entry.value === preset)!
  const allowed = !spec.feature || permits(spec.feature)

  // A show-scoped preset with no show picked is the single most likely way to
  // land on an empty document, so it self-corrects.
  useEffect(() => {
    if (!spec.needsShow || showId || sortedShows.length === 0) return
    const now = Date.now()
    const next = sortedShows.find((show) => show.date.toMillis() >= now)
    setShowId((next ?? sortedShows[sortedShows.length - 1]).id)
  }, [spec.needsShow, showId, sortedShows])

  const data: DocumentData | null = tour
    ? {
        tour,
        artist: artists.find((entry) => entry.id === tour.artistId) ?? null,
        shows: sortedShows,
        itinerary: itinerary.data,
        flights: flights.data,
        hotels: hotels.data,
        crew: crew.data,
        guests: guests.data,
      }
    : null

  const options: DocumentOptions = {
    preset,
    showId: spec.needsShow ? showId : null,
    notes,
    includeCrew,
    includeNotes,
    watermarked: plan.exportWatermark,
    branding: plan.exportBranding === 'none' ? null : { name: null },
  }

  const filename = useMemo(() => {
    if (!tour) return 'encore'
    const show = sortedShows.find((entry) => entry.id === showId)
    const parts = [tour.artist, tour.tourName, spec.label]
    if (spec.needsShow && show) parts.push(show.city)
    return parts
      .filter(Boolean)
      .join(' - ')
      .replace(/[^\w\s-]/g, '')
      .trim()
  }, [tour, sortedShows, showId, spec])

  if (tours.loading) {
    return <Page title="Export" />
  }

  if (tours.data.length === 0) {
    return (
      <Page title="Export">
        <Empty
          title="Nothing to export yet"
          message="Build a tour and its documents will be ready here."
        />
      </Page>
    )
  }

  return (
    <Page
      title="Export"
      eyebrow={tour ? tour.tourName : undefined}
      actions={
        <button
          type="button"
          className="btn btn-primary"
          disabled={!data || !allowed}
          onClick={() => printDocument(filename)}
        >
          <Icon name="export" size={15} /> Save as PDF
        </button>
      }
    >
      <div className="export-layout">
        {/* ------------------------------------------------------- controls */}
        <div className="stack" style={{ gap: 18 }}>
          <Field label="Tour">
            <select
              className="input"
              value={tourId ?? ''}
              onChange={(event) => {
                setTourId(event.target.value)
                setShowId(null)
              }}
            >
              {tours.data.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.tourName}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Document">
            <div className="stack" style={{ gap: 6 }}>
              {PRESETS.map((entry) => {
                const locked = Boolean(entry.feature) && !permits(entry.feature!)
                const on = entry.value === preset
                return (
                  <button
                    key={entry.value}
                    type="button"
                    className="panel"
                    onClick={() => setPreset(entry.value)}
                    style={{
                      textAlign: 'left',
                      padding: '11px 13px',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      border: `var(--hairline-width) solid ${on ? 'var(--accent)' : 'var(--hairline)'}`,
                      background: on ? 'var(--accent-wash)' : 'var(--panel)',
                      opacity: locked ? 0.72 : 1,
                    }}
                  >
                    <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                      <span className="row" style={{ gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>{entry.label}</span>
                        {locked && (
                          <span className="chip" style={{ color: 'var(--text-tertiary)' }}>
                            {entry.feature === 'tour_books' ? 'Pro' : 'Paid'}
                          </span>
                        )}
                      </span>
                      <span className="t-caption">{entry.blurb}</span>
                    </span>
                  </button>
                )
              })}
            </div>
          </Field>

          {spec.needsShow && (
            <Field label="Show">
              <select
                className="input"
                value={showId ?? ''}
                onChange={(event) => setShowId(event.target.value)}
              >
                <option value="">Pick a show</option>
                {sortedShows.map((show) => (
                  <option key={show.id} value={show.id}>
                    {formatDayLong(show.date, show.timezone)} · {show.city}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Include">
            <div className="stack" style={{ gap: 8 }}>
              <Check
                label="Crew list"
                checked={includeCrew}
                onChange={setIncludeCrew}
                hint={`${crew.data.length} on this tour`}
              />
              <Check label="A note" checked={includeNotes} onChange={setIncludeNotes} />
              {includeNotes && (
                <textarea
                  className="input"
                  rows={4}
                  value={notes}
                  placeholder="Anything the venue or the crew needs to know."
                  onChange={(event) => setNotes(event.target.value)}
                />
              )}
            </div>
          </Field>

          {loaded && !allowed && (
            <p className="panel panel-pad t-caption">
              <strong>{spec.label}</strong> isn&rsquo;t part of {plan.displayName}. Everything else
              here still exports.
            </p>
          )}

          {loaded && plan.exportWatermark && (
            <p className="t-caption">
              Documents carry a small &ldquo;Made with Encore&rdquo; footer on{' '}
              {plan.displayName}. Everything else about them is full quality.
            </p>
          )}
        </div>

        {/* -------------------------------------------------------- preview */}
        <div className="doc-preview">
          {data ? (
            /* `doc-print-root` is what the print stylesheet keeps; everything
               else on the page is hidden when the printer runs. */
            <div className="doc-print-root">
              <ExportDocument data={data} options={options} />
            </div>
          ) : (
            <Empty title="Pick a tour" message="Choose a tour to see its documents." />
          )}
        </div>
      </div>
    </Page>
  )
}

/* -------------------------------------------------------------------------- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 7 }}>
      <span className="t-eyebrow">{label}</span>
      {children}
    </div>
  )
}

function Check({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
  hint?: string
}) {
  return (
    <label className="row" style={{ gap: 9, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="grow">{label}</span>
      {hint && <span className="t-caption">{hint}</span>}
    </label>
  )
}
